import { useCallback, useEffect, useRef } from 'react';

interface ScrollSyncOptions {
  enabled: boolean;
  editorSelector: string;
  previewSelector: string;
}

export const useScrollSync = ({ enabled, editorSelector, previewSelector }: ScrollSyncOptions) => {
  const isScrollingRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);

  const syncScrollPosition = useCallback((source: 'editor' | 'preview', scrollTop: number, scrollHeight: number, clientHeight: number) => {
    if (!enabled || isScrollingRef.current) return;

    const scrollPercentage = scrollHeight > clientHeight ? scrollTop / (scrollHeight - clientHeight) : 0;
    
    isScrollingRef.current = true;
    
    const targetSelector = source === 'editor' ? previewSelector : editorSelector;
    const targetElement = document.querySelector(targetSelector);
    
    if (targetElement) {
      const targetScrollHeight = targetElement.scrollHeight;
      const targetClientHeight = targetElement.clientHeight;
      const targetScrollTop = (targetScrollHeight - targetClientHeight) * scrollPercentage;
      
      targetElement.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'auto' // Use auto to prevent infinite loops
      });
    }
    
    // Reset scrolling flag after a short delay
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      isScrollingRef.current = false;
    }, 150);
  }, [enabled, editorSelector, previewSelector]);

  const createScrollHandler = useCallback((source: 'editor' | 'preview') => {
    return (event: Event) => {
      const element = event.target as HTMLElement;
      if (element && enabled) {
        syncScrollPosition(source, element.scrollTop, element.scrollHeight, element.clientHeight);
      }
    };
  }, [syncScrollPosition, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const editorElement = document.querySelector(editorSelector);
    const previewElement = document.querySelector(previewSelector);

    if (!editorElement || !previewElement) return;

    const editorScrollHandler = createScrollHandler('editor');
    const previewScrollHandler = createScrollHandler('preview');

    // Add scroll listeners
    editorElement.addEventListener('scroll', editorScrollHandler, { passive: true });
    previewElement.addEventListener('scroll', previewScrollHandler, { passive: true });

    return () => {
      editorElement.removeEventListener('scroll', editorScrollHandler);
      previewElement.removeEventListener('scroll', previewScrollHandler);
      
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, editorSelector, previewSelector, createScrollHandler]);

  return {
    syncScrollPosition
  };
};